/**
 * API 에러를 현재 i18n 언어로 변환.
 *
 * BE 가 `X-Error-Code` 응답 헤더로 에러 코드를 보내면 client.js 인터셉터가
 * error.code 에 채워주며, 이 코드를 `errors.{code}` 번역 키로 매핑한다.
 * 코드가 없거나 매핑이 없으면 err.message(BE detail 한국어) → fallback 키 순서로 폴백.
 *
 * 도입 배경: BugHerd #123 (Consistent Inline Validation Error Across All Languages) +
 * BugHerd Korean leak 다국어 페이지의 alert/inline 메시지에 한글이 그대로 노출되던 문제.
 */
export function translateApiError(err, t, fallbackKey = 'common.error_generic') {
  const code = err?.code;
  if (code) {
    const key = `errors.${code}`;
    const translated = t(key, { defaultValue: '' });
    if (translated && translated !== key) return translated;
  }
  // BE detail 이 i18n 안 된 한국어 텍스트면 사용자 언어와 안 맞을 수 있으므로
  // 코드 매핑이 실패하면 명시적 fallback 키를 우선시한다.
  if (fallbackKey) {
    const fb = t(fallbackKey, { defaultValue: '' });
    if (fb && fb !== fallbackKey) return fb;
  }
  return err?.message || '';
}

/**
 * PortOne / PG 사유 문자열에서 외부 wrapper 제거.
 *
 * PortOne 은 PG 에러를 모달에 친절하게 노출하려고
 *   "결제 승인 과정에서 PG사 에러가 발생했습니다: {원본 사유}"
 * 형태로 wrapping 해서 내려준다. 우리 모달에서는 이 wrapper 가
 * 본문(잔액 부족 등) 앞에 노이즈로 보여서 한 단계만 잘라낸다.
 *
 * 내부 괄호 nesting(여러 단계)은 그대로 유지 — 사용자 / CS 가 모든 PG 메시지를
 * 함께 볼 수 있어야 디버깅이 가능하므로 손대지 않는다.
 */
function cleanPgReason(reason) {
  if (!reason) return '';
  return reason
    .replace(/^결제\s*(승인|진행)?\s*과정에서\s*PG사?\s*에러가?\s*발생[^:：]*[:：]\s*/, '')
    .trim();
}

/**
 * 결제 에러 메시지에 PG/서버가 내려준 사유 부착.
 *
 * 사용자가 "왜 취소/실패됐는지" 즉시 알 수 있도록 base 메시지 뒤에 "(사유: XXX)" 추가.
 *  - PortOne SDK 응답의 response.message → err.message 로 전달됨
 *  - 백엔드 400 detail (예: "결제 미완료 상태: failed") → err.message 로 전달됨
 *  - 사유가 base 와 동일하거나 비어있으면 부착 생략 (중복 방지)
 *  - PortOne 외부 wrapper("결제 승인 과정에서 PG사 에러가 발생했습니다:") 제거
 */
export function appendErrorReason(baseMessage, err, t) {
  const raw = (err?.message || '').trim();
  if (!raw || raw === baseMessage) return baseMessage;
  const reason = cleanPgReason(raw);
  if (!reason || reason === baseMessage) return baseMessage;
  const prefix = t('payment.reason_prefix', { defaultValue: '' });
  if (!prefix) return baseMessage;
  return `${baseMessage} (${prefix}: ${reason})`;
}
